import { useEffect, useState } from 'react';
import type { CustomerStatementResponse } from '@hesabat/contracts';

/**
 * كشف عميل أو مورد بشكل واحد — الطرف فى `party` بدل `customer`/`supplier`. حساب الدفعات
 * المقدمة وحساب كل سطر للعميل وحده، فهما اختياريان هنا.
 */
type Statement = Omit<CustomerStatementResponse, 'customer' | 'advanceAccountCode' | 'lines'> & {
  party: CustomerStatementResponse['customer'];
  advanceAccountCode?: string;
  lines: (Omit<CustomerStatementResponse['lines'][number], 'accountCode'> & {
    accountCode?: string;
  })[];
};
import { SOURCE_LABELS } from './AccountLedger';
import { api, ProblemError, UnauthorizedError } from './api';
import { downloadCsv } from './csv';
import { formatMoney, isZero } from './money';
import ReportActions from './ReportActions';

interface Props {
  companyId: string;
  kind: 'customer' | 'supplier';
  partyId: string;
  currency: string;
  onSessionLost: () => void;
}

function yearBounds(): { from: string; to: string } {
  const year = new Date().getFullYear();
  return { from: year + '-01-01', to: year + '-12-31' };
}

/**
 * كشف حساب عميل أو مورد — من الدفتر لا من المستندات: كل سطر على الحساب الرقابى يحمل
 * معرّف الطرف. العميل بطبيعة مدينة (الموجب مستحق عليه)، والمورد بطبيعة دائنة (الموجب مستحق له).
 */
export default function PartyStatement({
  companyId,
  kind,
  partyId,
  currency,
  onSessionLost,
}: Props) {
  const [period, setPeriod] = useState(yearBounds);
  const [statement, setStatement] = useState<Statement | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    if (period.from > period.to) {
      setError('تاريخ البداية بعد تاريخ النهاية.');
      return;
    }
    const load: Promise<Statement> =
      kind === 'customer'
        ? api
            .customerStatement(companyId, partyId, period.from, period.to)
            .then(({ customer, ...rest }) => ({ ...rest, party: customer }))
        : api
            .supplierStatement(companyId, partyId, period.from, period.to)
            .then(({ supplier, ...rest }) => ({ ...rest, party: supplier }));
    load
      .then((result) => {
        if (!cancelled) setStatement(result);
      })
      .catch((caught: unknown) => {
        if (cancelled) return;
        if (caught instanceof UnauthorizedError) onSessionLost();
        else if (caught instanceof ProblemError)
          setError(caught.problem.detail ?? caught.problem.title);
        else setError(caught instanceof Error ? caught.message : String(caught));
      });
    return () => {
      cancelled = true;
    };
  }, [companyId, kind, partyId, period, onSessionLost]);

  function exportCsv(): void {
    if (!statement) return;
    downloadCsv(
      (kind === 'customer' ? 'كشف-عميل-' : 'كشف-مورد-') +
        statement.party.code +
        '-' +
        statement.from +
        '-' +
        statement.to +
        '.csv',
      ['التاريخ', 'القيد', 'النوع', 'المرجع', 'البيان', 'مدين', 'دائن', 'الرصيد'],
      [
        [statement.from, '', '', '', 'رصيد افتتاحى', '', '', statement.openingBalance],
        ...statement.lines.map((line) => [
          line.postingDate,
          line.journalNumber ?? line.journalId,
          SOURCE_LABELS[line.sourceType] ?? line.sourceType,
          line.reference,
          line.memo,
          line.debit,
          line.credit,
          line.runningBalance,
        ]),
        [
          statement.to,
          '',
          '',
          '',
          'الإجمالى والرصيد الختامى',
          statement.totalDebit,
          statement.totalCredit,
          statement.closingBalance,
        ],
      ],
    );
  }

  return (
    <div className="panel">
      <div className="panel-head">
        <h2>
          كشف حساب{' '}
          {statement
            ? statement.party.code + ' — ' + statement.party.nameAr
            : kind === 'customer'
              ? 'عميل'
              : 'مورد'}
        </h2>
        {statement && <ReportActions onExport={exportCsv} />}
      </div>
      <div className="form-row">
        <label>
          من
          <input
            type="date"
            value={period.from}
            onChange={(event) => setPeriod((current) => ({ ...current, from: event.target.value }))}
          />
        </label>
        <label>
          إلى
          <input
            type="date"
            value={period.to}
            onChange={(event) => setPeriod((current) => ({ ...current, to: event.target.value }))}
          />
        </label>
      </div>
      {error && <p className="error">{error}</p>}
      {statement && (
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>التاريخ</th>
                <th>القيد</th>
                <th>النوع</th>
                <th>المرجع</th>
                <th>البيان</th>
                <th>مدين</th>
                <th>دائن</th>
                <th>الرصيد</th>
              </tr>
            </thead>
            <tbody>
              <tr className="opening">
                <td>{statement.from}</td>
                <td colSpan={6}>رصيد افتتاحى</td>
                <td className="num-cell">{formatMoney(statement.openingBalance)}</td>
              </tr>
              {statement.lines.map((line) => (
                <tr key={line.journalId + ':' + line.lineNumber}>
                  <td>{line.postingDate}</td>
                  <td className="mono">{line.journalNumber ?? line.journalId.slice(0, 8)}</td>
                  <td>{SOURCE_LABELS[line.sourceType] ?? line.sourceType}</td>
                  <td className="mono">{line.reference ?? ''}</td>
                  <td>{line.memo ?? ''}</td>
                  <td className="num-cell">{isZero(line.debit) ? '' : formatMoney(line.debit)}</td>
                  <td className="num-cell">
                    {isZero(line.credit) ? '' : formatMoney(line.credit)}
                  </td>
                  <td className="num-cell">{formatMoney(line.runningBalance)}</td>
                </tr>
              ))}
              {statement.lines.length === 0 && (
                <tr>
                  <td colSpan={8} className="sub">
                    لا حركة على هذا الطرف فى هذه الفترة.
                  </td>
                </tr>
              )}
            </tbody>
            <tfoot>
              <tr className="closing">
                <td>{statement.to}</td>
                <td colSpan={4}>الإجمالى والرصيد الختامى</td>
                <td className="num-cell total">{formatMoney(statement.totalDebit)}</td>
                <td className="num-cell total">{formatMoney(statement.totalCredit)}</td>
                <td className="num-cell total">{formatMoney(statement.closingBalance)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
      <p className="label">
        المبالغ بـ {currency}.{' '}
        {kind === 'customer'
          ? 'الموجب مستحق على العميل، والسالب دائن له (تحصيل لم يُخصص).'
          : 'الموجب مستحق للمورد، والسالب مدفوع له مقدمًا (سداد لم يُخصص).'}
      </p>
    </div>
  );
}
